import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useMyGuardianStudents } from '@/features/guardian/hooks';
import { ACCESS_SCOPE_LABELS, RELATIONSHIP_LABELS } from '@/features/guardian/labels';

/**
 * "Anak Saya" - `GET /guardians/me/students`, ACTIVE relationships only
 * (guardian.service.ts's own `status: 'ACTIVE'` filter; the backend never
 * returns INVITED/REVOKED rows here). Deliberately does NOT gate on
 * `role === 'Parent'`: any account holding an ACTIVE StudentGuardian link
 * reaches this page and sees data, including a Teacher or Admin who is
 * also a Guardian (ADR-025's Addendum, Decision I - "role name is
 * irrelevant").
 *
 * `GuardianStudentView` (guardian.service.ts) now also returns
 * `studentFullName` (G-9, Job 9), a scoped join through `Student.user` -
 * shown here as the card's primary label, replacing the raw `studentId`
 * this page previously had no display name available to show instead of.
 *
 * Each card links into the same Student Detail workspace every other role
 * uses (Phase G-1, Job 17) - `GET /students/:id`'s own Self/Child
 * narrowing (`authz/student-scope.ts`) already includes an ACTIVE-linked
 * child, so no separate Parent-only detail page is built.
 */
export function MyChildrenPage(): JSX.Element {
  const { data: children, isLoading, isError } = useMyGuardianStudents();

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      <h1 className="text-xl font-semibold">Anak Saya</h1>

      {isLoading && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat daftar anak.</AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && children !== undefined && children.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Belum ada anak yang terhubung dengan akun Anda.
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && children !== undefined && children.length > 0 && (
        <ul className="flex flex-col gap-3">
          {children.map((child) => (
            <li key={child.studentGuardianId}>
              <Link to={`/students/${child.studentId}`}>
                <Card className="transition-colors hover:bg-secondary/50">
                  <CardHeader className="flex flex-row items-center justify-between gap-4">
                    <CardTitle className="text-base">{child.studentFullName}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">{RELATIONSHIP_LABELS[child.relationshipType]}</Badge>
                    <Badge variant={child.accessScope === 'FULL_READ' ? 'default' : 'accent'}>
                      {ACCESS_SCOPE_LABELS[child.accessScope]}
                    </Badge>
                  </CardContent>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
